import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';

import { useHistory, useLocation } from 'react-router';

import { Loading } from '~/components/Loading';
import { WarningModal } from '~/components/WarningModal';
import {
  initToken,
  startCategory,
  start,
  validateCpf,
  validateExecuteSubscribe,
  enrollSubscribe,
  sendAnswerQuiz,
  sendOfferJourney,
  confirmQuizNegativeJourney,
  getMessage,
} from '~/services/JourneyService';
import type { PreJourney } from '~/@types/PreJourney';
import type { Journey } from '~/@types/Journey';
import type JourneyCurrentStep from '~/@types/JourneyCurrentStep';
import JourneyStepKind from '~/@types/JourneyStepKind';

import type SubscribeJourneyProps from '~/@types/SubscribeJourneyProps';

type ErrorResponse = {
  code: number;
  message: string;
  severity: 'warning' | 'error';
};
interface JourneyProps {
  children: ReactNode;
}

interface ContextProps {
  urlInitBase: string;
  preJourney: PreJourney;
  hasPreJourney: boolean;
  progress: number;
  journey: Journey;
  currentStep: JourneyCurrentStep;
  error: ErrorResponse;
  hideHeader: boolean;
  redirectInitApp: () => void;
  initPreJourney: () => Promise<void>;
  initJourney: () => Promise<void>;
  cleanError: () => void;
  validateCpfJourney: (cpf: string) => Promise<boolean>;
  validateSubscribeJourney: (
    props: SubscribeJourneyProps,
  ) => Promise<JourneyCurrentStep>;
  subscribeJourney: (props: SubscribeJourneyProps) => Promise<void>;
  sendAnswerCurrentQuestion: (answerId: string) => Promise<void>;
  sendOfferJourneySelected: (offerId: string) => Promise<void>;
  sendConsentQuizNegative: () => Promise<void>;
  onHideHeader: () => void;
}

const getPathToKind = (kind: string): string => {
  switch (kind) {
    case JourneyStepKind.SubscriberEnrollment:
      return '/';
    case JourneyStepKind.IntroTermConfirm:
      return '/intro';
    case JourneyStepKind.Quiz:
      return '/quiz';
    case JourneyStepKind.QuizNegativeIdentification:
      return '/quiznegative';
    case JourneyStepKind.OfferSelection:
      return '/offer';
    case JourneyStepKind.JourneyClosed:
      return '/journeyclosed';
    case JourneyStepKind.RedirectToPortalDigital:
      return '/redirectPortalDigital';
    case JourneyStepKind.RedirectToIframe:
      return '/internal';
    default:
      return '/';
  }
};

const getKindToPath = (path: string): string => {
  switch (path) {
    case '/':
      return JourneyStepKind.SubscriberEnrollment;
    case '/intro':
      return JourneyStepKind.IntroTermConfirm;
    case '/quiz':
      return JourneyStepKind.Quiz;
    case '/quiznegative':
      return JourneyStepKind.QuizNegativeIdentification;
    case '/offer':
      return JourneyStepKind.OfferSelection;
    case '/journeyclosed':
      return JourneyStepKind.JourneyClosed;
    case '/redirectPortalDigital':
      return JourneyStepKind.RedirectToPortalDigital;
    default:
      return '';
  }
};

const JourneyContext = createContext<ContextProps>({} as ContextProps);

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function JourneyProvider({ children }: JourneyProps) {
  const history = useHistory();
  const query = useQuery();
  const location = useLocation();

  const [urlInitBase, setUrlInitBase] = useState('');
  const [loading, setLoading] = useState(false);

  const [hasPreJourney, setHasPreJourney] = useState(false);
  const [preJourney, setPreJourney] = useState<PreJourney>(null);
  const [progress, setProgress] = useState<number>(0);
  const calledRef = useRef(false);
  const progressIntervalRef = useRef<NodeJS.Timeout>();

  const [journey, setJourney] = useState<Journey>(null);
  const [currentStep, setCurrentStep] = useState<JourneyCurrentStep>(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [error, setError] = useState<ErrorResponse>(null);
  const [hideHeader, setHideHeader] = useState(false);

  function onHideHeader() {
    setHideHeader(true);
  }

  const updateJourney = useCallback(
    (journeyUpdate: Journey) => {
      setJourney(journeyUpdate);
    },
    [setJourney],
  );

  const redirectCurrentStep = useCallback(
    (currentStepChange: JourneyCurrentStep, journeyUpdate: Journey = null) => {
      if (journeyUpdate) {
        setJourney(journeyUpdate);
      }

      setCurrentStep(currentStepChange);

      if (
        currentStepChange.kind === JourneyStepKind.RedirectToPortalDigital &&
        !currentStepChange.combo
      ) {
        window.location.href = currentStepChange.redirectUrl;
      } else {
        const path = getPathToKind(currentStepChange.kind);
        if (path === '/internal') {
          if (
            !currentStepChange.combo ||
            !currentStepChange.offer ||
            !currentStepChange.offer.selectedCard
          ) {
            onHideHeader();
          }
        }
        history.push(path);
      }
    },
    [history],
  );

  function redirectInitApp() {
    window.location.href = urlInitBase;
  }
  /* delay da nova pagina inicial */
  const clearProgressInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = undefined;
    }
  };

  const startProgressAnimation = (duration: number) => {
    clearProgressInterval();

    const startTime = Date.now();
    const endTime = startTime + duration;

    progressIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const remaining = endTime - now;

      if (remaining <= 0) {
        clearProgressInterval();
        setProgress(100);
        return;
      }

      const elapsed = duration - remaining;
      const currentProgress = Math.min(100, (elapsed / duration) * 100);
      setProgress(currentProgress);
    }, 50);
  };

  const initPreJourney = useCallback(async () => {
    if (calledRef.current) return;
    calledRef.current = true;
    setLoading(false);
    try {
      startProgressAnimation(3000);
      const dma = getDMA() === '1';
      const source = dma ? 'DMA' : 'WEB';
      const token = getToken();
      await initToken(token);
      const [response] = await Promise.all([
        startCategory(source),
        new Promise(resolve => setTimeout(resolve, 3000)),
      ]);
      setPreJourney(response);
      setHasPreJourney(true);
    } catch (e) {
      clearProgressInterval();
      await validateError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  async function initJourney() {
    setLoading(true);
    try {
      const dma = getDMA() === '1';
      const source = dma ? 'DMA' : 'WEB';
      const resultJourney = await start(source);
      redirectCurrentStep(resultJourney.currentStep, resultJourney);
    } catch (e) {
      await validateError(e);
    }
    setLoading(false);
  }

  function getToken(): string {
    if (process.env.NODE_ENV === 'development') {
      return localStorage.getItem('@CadastroWebClaro:token');
    }
    return query.get('token') || '';
  }

  function getDMA(): string {
    return query.get('dma') || '0';
  }

  async function validateError(resultError) {
    const { status, data } = resultError.response;

    const dataError = {
      message: '',
      ...data,
    };

    if (status !== 422) {
      let code = 1; // 1 (InternalError)
      if (status === 403) {
        code = 4; // 4 (MissingOrInvalidMsisdn)
      }
      try {
        const [{ message }] = await getMessage(code);
        dataError.message = message;
      } catch {
        dataError.message =
          'Prezado cliente, no momento os nossos canais de cadastro estão indisponíveis. Pedimos desculpas pelo transtorno.';
      }
    }

    if (status === 422 && dataError.severity === 'warning') {
      setError(dataError);
      setShowWarningModal(true);
    } else {
      setError(dataError);
      history.push('/error');
    }
  }

  useEffect(() => {
    if (calledRef.current) return;
    const { host } = window.location;
    setUrlInitBase(`http://${host}`);
    async function loadJourney() {
      setLoading(true);
      try {
        const token = getToken();
        await initToken(token);
        history.push('/');
      } catch (e) {
        await validateError(e);
      }
      const introStep = sessionStorage.getItem('introStep');
      if (introStep) {
        initJourney();
      }
      setLoading(false);
    }
    loadJourney();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cleanError() {
    setError(null);
  }

  async function validateCpfJourney(cpf: string): Promise<boolean> {
    setLoading(true);
    let result = false;
    const { id: journeyId } = journey;
    try {
      const resultValidate = await validateCpf(journeyId, cpf);
      if (resultValidate) {
        result = true;
        if (
          resultValidate.currentStep.kind !==
          JourneyStepKind.SubscriberEnrollment
        ) {
          redirectCurrentStep(resultValidate.currentStep, resultValidate);
        }
      }
    } catch (e) {
      await validateError(e);
    }
    setLoading(false);
    return result;
  }

  async function validateSubscribeJourney(
    props: SubscribeJourneyProps,
  ): Promise<JourneyCurrentStep> {
    setLoading(true);
    const { id: journeyId } = journey;
    try {
      const validateExecuteSubscribeResult = await validateExecuteSubscribe(
        journeyId,
        props,
      );
      if (validateExecuteSubscribeResult) {
        if (
          validateExecuteSubscribeResult.currentStep.kind !==
          JourneyStepKind.SubscriberEnrollment
        ) {
          redirectCurrentStep(
            validateExecuteSubscribeResult.currentStep,
            validateExecuteSubscribeResult,
          );
        }
        setLoading(false);
        return validateExecuteSubscribeResult.currentStep;
      }
    } catch (e) {
      await validateError(e);
    }
    setLoading(false);

    return null;
  }

  async function subscribeJourney(props: SubscribeJourneyProps): Promise<void> {
    setLoading(true);
    const { id: journeyId } = journey;
    try {
      const resultEnroll = await enrollSubscribe(journeyId, props);
      redirectCurrentStep(resultEnroll.currentStep, resultEnroll);
    } catch (e) {
      await validateError(e);
    }
    setLoading(false);
  }

  async function sendAnswerCurrentQuestion(answerId: string): Promise<void> {
    setLoading(true);
    const { id: journeyId } = journey;
    try {
      const sendAnswerQuizResult = await sendAnswerQuiz(journeyId, answerId);
      if (sendAnswerQuizResult) {
        redirectCurrentStep(
          sendAnswerQuizResult.currentStep,
          sendAnswerQuizResult,
        );
      }
    } catch (e) {
      await validateError(e);
    }
    setLoading(false);
  }

  async function sendOfferJourneySelected(offerId: string): Promise<void> {
    setLoading(true);
    const { id: journeyId } = journey;
    try {
      const sendOfferJourneyResult = await sendOfferJourney(journeyId, offerId);
      if (sendOfferJourneyResult) {
        redirectCurrentStep(
          sendOfferJourneyResult.currentStep,
          sendOfferJourneyResult,
        );
      }
    } catch (e) {
      await validateError(e);
    }
    setLoading(false);
  }

  async function sendConsentQuizNegative(): Promise<void> {
    setLoading(true);
    const { id: journeyId } = journey;
    try {
      const result = await confirmQuizNegativeJourney(journeyId);
      if (result) {
        redirectCurrentStep(result.currentStep, result);
      }
    } catch (e) {
      await validateError(e);
    }
    setLoading(false);
  }

  return (
    <JourneyContext.Provider
      value={{
        urlInitBase,
        preJourney,
        hasPreJourney,
        progress,
        journey,
        currentStep,
        error,
        hideHeader,
        redirectInitApp,
        initPreJourney,
        initJourney,
        validateCpfJourney,
        validateSubscribeJourney,
        subscribeJourney,
        sendAnswerCurrentQuestion,
        sendOfferJourneySelected,
        sendConsentQuizNegative,
        cleanError,
        onHideHeader,
      }}
    >
      <>
        {loading && <Loading />}
        {children}
        <WarningModal
          show={showWarningModal}
          message={error ? error.message : ''}
          onClose={() => {
            setShowWarningModal(false);
            setError(null);
          }}
        />
      </>
    </JourneyContext.Provider>
  );
}

export function useJourney() {
  const context = useContext(JourneyContext);
  return context;
}
